import { useCallback, useEffect, useMemo } from 'react';
import {
  ComparisonTable,
  Flex,
  Heading,
  Modal,
  Text,
} from '@highereducation/hen-ui';
import { useComparison } from '../../providers/ComparisonProvider';
import { useResultsPageConfig } from '../../providers/ResultsPageConfigProvider';
import useFlowTrackingEvents from '../../hooks/useFlowTrackingEvents';
import { useTracking } from '../../providers/TrackingProvider';
import type { Result } from '../../types/result';
import {
  emitComparisonProgramRemoved,
  emitComparisonProductClicked,
  emitComparisonProductLoaded,
  emitComparisonProductViewed,
} from './analytics';
import {
  comparisonRowDefsForConfig,
  resultsToComparisonItems,
} from './mapping';

type Props = {
  /**
   * @deprecated Retained for prop-shape compatibility with the legacy
   * `ComparisonModal`. The hen-ui `Button` used inside `ComparisonTable`
   * does not expose a pill/square toggle, so this prop is now a no-op.
   * Safe to drop once the parent variant stops passing it.
   */
  squareButtons?: boolean;
  onProductClick?: (result: Result, skipDuplicateCheck?: boolean) => void;
  submittedResults?: { programId?: number; schoolId?: string }[];
};

/**
 * Comparison modal — hen-ui `Modal` + `ComparisonTable` composition.
 *
 * The body is now driven by `ComparisonTable` which handles column layout,
 * mobile carousel, sticky header band, empty-slot column, and per-column
 * `onProductViewed`/`onProductClicked`/`onCtaClick` hooks. Data shape and
 * cell renderers are owned by `./mapping.ts`. The legacy hand-rolled
 * `Card[]` row, the equal-height `useEffect` measurement, and the
 * "Choose more schools" sticky footer have all been retired.
 *
 * Product analytics: each compared program emits `ProductLoaded` when the
 * modal opens (parity with the imperative `Card`'s mount-time event) and
 * `ProductViewed` when its column becomes visible (driven by
 * `ComparisonTable`'s `onProductViewed` callback). The modal does NOT emit
 * an `ElementViewed`/ComparisonViewed beam on open — see `./analytics.ts`.
 */
const ComparisonModal = ({
  // squareButtons is intentionally accepted but unused — see JSDoc above.
  squareButtons: _squareButtons,
  onProductClick,
  submittedResults = [],
}: Props) => {
  const {
    programsToCompare,
    isComparisonModalOpen,
    maxItems,
    toggleComparisonModal,
    removeProgramFromCompare,
    dslConfigId,
  } = useComparison();
  const dslConfigVersion = useTracking()?.stepCorrelation.dslConfigVersion;
  const { getComparisonConfig, getComparisonCopy } = useResultsPageConfig();
  const comparisonConfig = getComparisonConfig();
  const comparisonCopy = getComparisonCopy();

  // Tolerant read: `eventContext` is `undefined` outside a
  // `DynamicExperienceProvider` (legacy render paths / unit tests), in
  // which case the product emitters no-op. Mirrors the imperative `Card`,
  // which sources its product payloads from the same flow context.
  const { eventContext } = useFlowTrackingEvents();

  const handleClose = useCallback(() => {
    toggleComparisonModal(false);
  }, [toggleComparisonModal]);

  const isProgramSubmitted = useCallback(
    (programId: number) =>
      submittedResults.some((submission) => submission.programId === programId),
    [submittedResults],
  );

  const handleProductClick = useCallback(
    (result: Result) => {
      const position = programsToCompare.findIndex(
        (program) => program.programId === result.programId,
      );
      emitComparisonProductClicked({
        result,
        eventContext,
        position: position === -1 ? 0 : position + 1,
        isProgramSubmitted: isProgramSubmitted(result.programId),
        dslConfigId,
        dslConfigVersion,
      });
      toggleComparisonModal(false);
      onProductClick?.(result, false);
    },
    [
      onProductClick,
      toggleComparisonModal,
      programsToCompare,
      eventContext,
      isProgramSubmitted,
      dslConfigId,
      dslConfigVersion,
    ],
  );

  // Fire the remove `ElementClicked` beam whenever the user clicks the
  // remove (X) control on a comparison card, then remove the program.
  // When the last program is removed the modal also closes.
  const handleRemoveProgram = useCallback(
    (programId: number) => {
      emitComparisonProgramRemoved({ eventContext, dslConfigId });
      removeProgramFromCompare(programId);
      if (programsToCompare.length <= 1) {
        toggleComparisonModal(false);
      }
    },
    [
      eventContext,
      dslConfigId,
      programsToCompare.length,
      removeProgramFromCompare,
      toggleComparisonModal,
    ],
  );

  const items = useMemo(
    () =>
      resultsToComparisonItems(programsToCompare, {
        onProductClick: handleProductClick,
        onRemove: handleRemoveProgram,
        isProgramSubmitted,
        config: comparisonConfig,
      }),
    [
      programsToCompare,
      handleProductClick,
      handleRemoveProgram,
      isProgramSubmitted,
      comparisonConfig,
    ],
  );

  const rowDefs = useMemo(
    () => comparisonRowDefsForConfig(comparisonConfig),
    [comparisonConfig],
  );
  const heading =
    comparisonCopy.heading ?? comparisonCopy.title ?? 'Compare Programs';
  const subtitle =
    comparisonCopy.subtitle ??
    `Compare up to ${maxItems} credit programs from our partners using the side-by-side comparison below.`;
  const emptySlotLabel =
    comparisonCopy.placeholderText ?? 'Add another program';

  // Fire `ProductLoaded` for every compared program once per open
  // transition (parity with the imperative `Card`'s mount-time event).
  // Intentionally depends only on `isComparisonModalOpen` so the events
  // don't re-fire as the user adds/removes a program while the modal is
  // open; the effect reads the live selection at the moment it opens.
  useEffect(() => {
    if (!isComparisonModalOpen) return;
    programsToCompare.forEach((result, index) => {
      emitComparisonProductLoaded({
        result,
        eventContext,
        position: index + 1,
        isProgramSubmitted: isProgramSubmitted(result.programId),
        dslConfigId,
        dslConfigVersion,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComparisonModalOpen, dslConfigVersion]);

  // `ProductViewed` is visibility-driven: `ComparisonTable` invokes
  // `onProductViewed` once per column the first time it is visible while
  // `inView` is true. Map the column id back to its program and emit.
  const handleProductViewed = useCallback(
    (id: string) => {
      const index = programsToCompare.findIndex(
        (program) => String(program.programId) === id,
      );
      if (index === -1) return;
      const result = programsToCompare[index];
      emitComparisonProductViewed({
        result,
        eventContext,
        position: index + 1,
        isProgramSubmitted: isProgramSubmitted(result.programId),
        dslConfigId,
        dslConfigVersion,
      });
    },
    [
      programsToCompare,
      eventContext,
      isProgramSubmitted,
      dslConfigId,
      dslConfigVersion,
    ],
  );

  return (
    <Modal
      open={isComparisonModalOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <Modal.Content size='xl' className='p-0 max-h-[95vh] overflow-y-auto'>
        <Modal.Header className='sr-only'>
          <Modal.Title>{heading}</Modal.Title>
        </Modal.Header>
        <Flex direction='column' gap='md' className='px-lg py-md'>
          <Flex direction='column' gap='xxs'>
            <Heading variant='h3'>{heading}</Heading>
            <Text variant='b2' color='subtext'>
              {subtitle}
            </Text>
          </Flex>
          <ComparisonTable
            styleVersion='v1'
            items={items}
            rowDefs={rowDefs}
            maxItems={maxItems}
            visibleItems={maxItems}
            emptySlot={{
              label: emptySlotLabel,
              onClick: handleClose,
              ariaLabel: 'Close to add another program to compare',
            }}
            inView={isComparisonModalOpen}
            onProductViewed={handleProductViewed}
          />
        </Flex>
      </Modal.Content>
    </Modal>
  );
};

export default ComparisonModal;
